import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import {
  disabledSectionNotice,
  isSectionEnabled,
} from 'app/core/environment/build-features';
import type { ConsoleSection } from 'environments/environment.model';

/**
 * Factory for a guard that blocks a {@link ConsoleSection} the current build
 * does not expose (`environment.enabledSections`).
 *
 * This is the *build* half of the two-stage check on product routes; the other
 * is {@link productActivationGuard}, which asks whether the customer has
 * activated the product. They are deliberately separate questions: "this
 * console cannot talk to the backend behind that section" is not something the
 * user can fix by activating anything.
 *
 * On a blocked navigation the user is sent to `/unavailable/:section`, which
 * exists **only when the build defines `disabledSectionNotice`** — i.e. never
 * in a release build. Without a notice there is nothing to explain, so the
 * redirect falls back to the project list.
 *
 * A `RedirectCommand` is returned rather than firing `router.navigate()`, so
 * the redirect is part of the same navigation instead of racing it.
 *
 * @param section - The section this route belongs to.
 */
export function sectionEnabledGuard(section: ConsoleSection): CanActivateFn {
  return () => {
    if (isSectionEnabled(section)) {
      return true;
    }

    const router = inject(Router);
    const target = disabledSectionNotice()
      ? ['/unavailable', section]
      : ['/projects'];

    return new RedirectCommand(router.createUrlTree(target));
  };
}
